import { fromBase64Url, randomBytes, toBase64Url } from './bytes.js';
import { domainSeparated, hashCanonical } from './hash.js';
import { signP256, verifyP256 } from './keys.js';
/**
 * Standing authorization for autonomous dispatch (docs/signing.md, Appendix A).
 *
 * The hub cannot sign commands, so it cannot start runs on its own. A *dispatch grant* is a
 * passkey-approved, device-signed statement that lets the hub ask daemons to start/feed/stop runs
 * for tasks inside a bounded scope (machines, projects, optionally workstreams, kinds, rate
 * limits, expiry). One passkey tap covers every machine in the grant. Daemons verify it offline
 * against their pinned passkeys exactly like any other passkey-gated statement.
 *
 * A grant revocation only *reduces* authority, so a device signature is enough (no tap).
 */
export const GRANT_DOMAIN = {
    /** Passkey challenge and device signature over a grant statement. */
    grant: 'viko/dispatch-grant/v1',
    /** Device signature over a grant revocation. */
    revocation: 'viko/dispatch-grant-revocation/v1',
};
export const DISPATCH_KINDS = ['run.start', 'run.send', 'run.stop'];
export const DEFAULT_GRANT_LIMITS = {
    maxStartsPerHour: 12,
    maxConcurrentRuns: 6,
    maxPromptBytes: 32_000,
};
export const DEFAULT_GRANT_TTL_MS = 7 * 24 * 60 * 60_000;
export const MAX_GRANT_TTL_MS = 7 * 24 * 60 * 60_000;
export const GRANT_CLOCK_SKEW_MS = 60_000;
export function createDispatchGrantStatement(input) {
    const issuedAt = input.now ?? Date.now();
    const s = {
        v: 1,
        type: 'viko.dispatch-grant',
        grantId: input.grantId ?? globalThis.crypto.randomUUID(),
        machineIds: [...new Set(input.machineIds)].sort(),
        projectIds: [...new Set(input.projectIds)].sort(),
        kinds: [...new Set(input.kinds ?? DISPATCH_KINDS)].sort(),
        limits: { ...DEFAULT_GRANT_LIMITS, ...input.limits },
        deviceKeyId: input.deviceKeyId,
        nonce: toBase64Url(randomBytes(16)),
        issuedAt,
        expiresAt: issuedAt + (input.ttlMs ?? DEFAULT_GRANT_TTL_MS),
    };
    if (input.workstreamIds)
        s.workstreamIds = [...new Set(input.workstreamIds)].sort();
    return s;
}
/** Bytes the device key signs. */
export function grantSigningInput(statement) {
    return domainSeparated(GRANT_DOMAIN.grant, statement);
}
/** WebAuthn challenge (32 bytes) for the one passkey tap that approves the grant. */
export function createDispatchGrantChallenge(statement) {
    return hashCanonical(GRANT_DOMAIN.grant, statement);
}
export async function signDispatchGrant(statement, device, assertion) {
    if (statement.deviceKeyId !== device.deviceKeyId)
        throw new Error('grant device mismatch');
    const signature = toBase64Url(await signP256(device.privateKey, grantSigningInput(statement)));
    return { statement, signature, assertion };
}
export function createGrantRevocation(input) {
    return {
        v: 1,
        type: 'viko.dispatch-grant-revocation',
        grantIds: [...new Set(input.grantIds)].sort(),
        deviceKeyId: input.deviceKeyId,
        nonce: toBase64Url(randomBytes(16)),
        issuedAt: input.now ?? Date.now(),
    };
}
export async function signGrantRevocation(statement, device) {
    const signature = toBase64Url(await signP256(device.privateKey, domainSeparated(GRANT_DOMAIN.revocation, statement)));
    return { statement, signature };
}
const isStrArr = (a, min = 0) => Array.isArray(a) && a.length >= min && a.every((x) => typeof x === 'string' && x.length > 0);
const posInt = (n) => Number.isSafeInteger(n) && n > 0;
export function isDispatchGrantStatement(v) {
    if (typeof v !== 'object' || v === null)
        return false;
    const s = v;
    const l = s.limits;
    return (s.v === 1 &&
        s.type === 'viko.dispatch-grant' &&
        typeof s.grantId === 'string' &&
        s.grantId.length > 0 &&
        isStrArr(s.machineIds, 1) &&
        isStrArr(s.projectIds, 1) &&
        (s.workstreamIds === undefined || isStrArr(s.workstreamIds, 1)) &&
        isStrArr(s.kinds, 1) &&
        s.kinds.every((k) => DISPATCH_KINDS.includes(k)) &&
        typeof l === 'object' &&
        l !== null &&
        posInt(l.maxStartsPerHour) &&
        posInt(l.maxConcurrentRuns) &&
        posInt(l.maxPromptBytes) &&
        typeof s.deviceKeyId === 'string' &&
        typeof s.nonce === 'string' &&
        Number.isSafeInteger(s.issuedAt) &&
        Number.isSafeInteger(s.expiresAt));
}
/**
 * Verify a grant: shape, device endorsed and not revoked, device signature, passkey approval over
 * the statement, validity window (≤ MAX_GRANT_TTL_MS). Scope checks per dispatch are separate
 * (`checkDispatch` in `@viko/protocol` work.ts).
 */
export async function verifyDispatchGrant(grant, trust, opts = {}) {
    const now = opts.now ?? Date.now();
    const skew = opts.clockSkewMs ?? GRANT_CLOCK_SKEW_MS;
    const s = grant?.statement;
    if (!isDispatchGrantStatement(s) || typeof grant.signature !== 'string' || !grant.assertion) {
        return { ok: false, reason: 'malformed' };
    }
    if (s.expiresAt <= s.issuedAt)
        return { ok: false, reason: 'malformed', detail: 'window' };
    if (s.expiresAt - s.issuedAt > (opts.maxTtlMs ?? MAX_GRANT_TTL_MS)) {
        return { ok: false, reason: 'ttl-too-long' };
    }
    if (s.issuedAt > now + skew)
        return { ok: false, reason: 'not-yet-valid' };
    if (now > s.expiresAt + skew)
        return { ok: false, reason: 'expired' };
    const lookup = trust.lookupDevice(s.deviceKeyId, now);
    if (lookup.status === 'revoked')
        return { ok: false, reason: 'revoked' };
    if (lookup.status !== 'ok' || !('device' in lookup))
        return { ok: false, reason: 'unknown-device' };
    if (lookup.device.session)
        return { ok: false, reason: 'session-device' };
    let sig;
    try {
        sig = fromBase64Url(grant.signature);
    }
    catch {
        return { ok: false, reason: 'bad-signature', detail: 'encoding' };
    }
    if (!(await verifyP256(lookup.device.publicKey, sig, grantSigningInput(s)))) {
        return { ok: false, reason: 'bad-signature' };
    }
    const approval = await trust.verifyPasskeyApproval(grant.assertion, await createDispatchGrantChallenge(s), opts.trackSignCount ? { trackSignCount: true } : {});
    if (!approval.ok)
        return { ok: false, reason: 'bad-approval', detail: approval.reason };
    return { ok: true, statement: s, credentialId: approval.credential.credentialId };
}
/**
 * Verify a device-signed grant revocation. Any endorsed, non-revoked device may revoke any grant
 * (revocation only removes authority).
 */
export async function verifyGrantRevocation(rev, trust, opts = {}) {
    const s = rev?.statement;
    if (s?.v !== 1 ||
        s.type !== 'viko.dispatch-grant-revocation' ||
        !isStrArr(s.grantIds, 1) ||
        typeof s.deviceKeyId !== 'string' ||
        typeof rev.signature !== 'string') {
        return { ok: false, reason: 'malformed' };
    }
    const lookup = trust.lookupDevice(s.deviceKeyId, opts.now ?? Date.now());
    if (lookup.status === 'revoked')
        return { ok: false, reason: 'revoked' };
    if (lookup.status !== 'ok' || !('device' in lookup))
        return { ok: false, reason: 'unknown-device' };
    let sig;
    try {
        sig = fromBase64Url(rev.signature);
    }
    catch {
        return { ok: false, reason: 'bad-signature' };
    }
    const ok = await verifyP256(lookup.device.publicKey, sig, domainSeparated(GRANT_DOMAIN.revocation, s));
    return ok
        ? { ok: true, grantIds: s.grantIds, deviceKeyId: s.deviceKeyId }
        : { ok: false, reason: 'bad-signature' };
}
//# sourceMappingURL=grant.js.map